import { Box, Button, Flex, Input, Select, SimpleGrid, Text } from '@chakra-ui/react';
import { HelperTooltip } from 'components/Tooltip';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import DecimalInput from '../DecimalInput';
import PercentInput from '../PercentInput';
import { fmtIntegerInput, parseIntegerInput } from '../calc/formatters';

function FieldLabel({ children, hint }) {
  return (
    <Flex mb={1} align="center">
      <Text fontSize="sm">{children}</Text>
      {hint ? (
        <Box as="span" display="inline-flex" aria-label={`${children} help`}>
          <HelperTooltip content={hint} />
        </Box>
      ) : null}
    </Flex>
  );
}

FieldLabel.propTypes = {
  children: PropTypes.node.isRequired,
  hint: PropTypes.string,
};

function MonthInput({ value, onChange, index }) {
  const [draft, setDraft] = useState(String(value ?? ''));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setDraft(String(value ?? ''));
    }
  }, [value, focused]);

  return (
    <Input
      type="text"
      inputMode="numeric"
      size="sm"
      aria-label={`Anchor month ${index + 1}`}
      value={focused ? draft : String(value ?? '')}
      onFocus={() => {
        setFocused(true);
        setDraft(String(value ?? ''));
      }}
      onChange={(event) => {
        const raw = event.target.value.replace(/[^\d]/g, '');
        setDraft(raw);
        if (raw === '') {
          return;
        }
        onChange(Number(raw));
      }}
      onBlur={() => {
        setFocused(false);
        onChange(draft === '' ? 0 : Number(draft));
      }}
    />
  );
}

MonthInput.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

function FprInputs({ value, onChange }) {
  const firstAnchor = value.anchors[0];
  const momFloor = value.momStep * (value.borderlinePct / 100);
  const firstAnchorFloor = firstAnchor
    ? firstAnchor.roasPct * (value.borderlinePct / 100)
    : null;
  const collectionTake = (value.fundingPct / 100) * (value.revenueSharePct / 100) * 100;

  const update = (patch) => onChange({ ...value, ...patch });

  const updateAnchor = (index, patch) => {
    const anchors = value.anchors.map((anchor, anchorIndex) =>
      anchorIndex === index ? { ...anchor, ...patch } : anchor
    );
    update({ anchors });
  };

  const addAnchor = () => {
    const last = value.anchors[value.anchors.length - 1];
    update({
      anchors: [
        ...value.anchors,
        {
          month: last ? last.month + 6 : 0,
          roasPct: last ? Math.min(300, last.roasPct + 25) : 12,
        },
      ],
    });
  };

  const removeAnchor = (index) => {
    update({ anchors: value.anchors.filter((_, anchorIndex) => anchorIndex !== index) });
  };

  return (
    <Box className="section-stack">
      <Box className="card">
        <Flex className="section-header" align="center" gap={3}>
          <span className="step-num">1</span>
          <Text fontWeight="600">Anchors</Text>
          <Box as="span" display="inline-flex" aria-label="Anchors help">
            <HelperTooltip content="0-indexed month and the minimum the reference revenue vector must reach (revenue × revenue share). A month 0 anchor is a hard gate on the reference: it must be met in full or there is no funding." />
          </Box>
          <Button size="sm" variant="outline" onClick={addAnchor} ml="auto">
            Add month
          </Button>
        </Flex>
        <Box p={5}>
          <Flex direction="column" gap={2}>
            {value.anchors.map((anchor, index) => (
              <Flex key={`anchor-row-${index}`} gap={2} align="center">
                <Box flex="1">
                  {index === 0 ? <FieldLabel>Month</FieldLabel> : null}
                  <MonthInput
                    index={index}
                    value={anchor.month}
                    onChange={(month) => updateAnchor(index, { month })}
                  />
                </Box>
                <Box flex="1">
                  {index === 0 ? <FieldLabel>Minimum ROAS %</FieldLabel> : null}
                  <PercentInput
                    size="sm"
                    value={anchor.roasPct}
                    onChange={(roasPct) => updateAnchor(index, { roasPct })}
                    fractionDigits={1}
                  />
                </Box>
                <Button
                  size="sm"
                  variant="ghost"
                  mt={index === 0 ? 6 : 0}
                  onClick={() => removeAnchor(index)}
                  isDisabled={value.anchors.length <= 1}
                >
                  Remove
                </Button>
              </Flex>
            ))}
          </Flex>
        </Box>
      </Box>

      <Box className="card">
        <Flex className="section-header" align="center" gap={3}>
          <span className="step-num">2</span>
          <Text fontWeight="600">Month over month</Text>
        </Flex>
        <Box p={5}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <Box>
              <FieldLabel hint="Covenant applies to the reference revenue vector, not the revenue vector.">
                Minimum step
              </FieldLabel>
              <DecimalInput
                size="sm"
                value={value.momStep}
                onChange={(momStep) => update({ momStep })}
                fractionDigits={2}
              />
            </Box>
            <Box>
              <FieldLabel>Measured in</FieldLabel>
              <Select
                size="sm"
                value={value.momMode}
                onChange={(event) => update({ momMode: event.target.value })}
              >
                <option value="PP">Points</option>
                <option value="REL">% of level</option>
              </Select>
            </Box>
            <Box>
              <FieldLabel hint="No growth required once the vector reaches this ROAS.">
                No growth above
              </FieldLabel>
              <PercentInput
                size="sm"
                value={value.capPct}
                onChange={(capPct) => update({ capPct })}
                fractionDigits={1}
              />
            </Box>
          </SimpleGrid>
        </Box>
      </Box>

      <Box className="card">
        <Flex className="section-header" align="center" gap={3}>
          <span className="step-num">3</span>
          <Text fontWeight="600">What you collect</Text>
        </Flex>
        <Box p={5}>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            <Box>
              <FieldLabel
                hint={
                  firstAnchorFloor == null
                    ? `MoM ${value.momStep}% × ${value.borderlinePct}% = ${momFloor.toFixed(2)}% floor. At or below the floor is total recall.`
                    : firstAnchor.month === 0
                      ? `MoM ${value.momStep}% × ${value.borderlinePct}% = ${momFloor.toFixed(2)}%. Month 0 anchor ${firstAnchor.roasPct}% must be met in full or there is no funding. Later anchors use the ${value.borderlinePct}% floor.`
                      : `MoM ${value.momStep}% × ${value.borderlinePct}% = ${momFloor.toFixed(2)}%. Month ${firstAnchor.month} anchor ${firstAnchor.roasPct}% × ${value.borderlinePct}% = ${firstAnchorFloor.toFixed(2)}%. Stay above both floors.`
                }
              >
                Borderline
              </FieldLabel>
              <PercentInput
                size="sm"
                value={value.borderlinePct}
                onChange={(borderlinePct) => update({ borderlinePct })}
                fractionDigits={0}
              />
            </Box>
            <Box>
              <FieldLabel>Funding %</FieldLabel>
              <PercentInput
                size="sm"
                value={value.fundingPct}
                onChange={(fundingPct) => update({ fundingPct })}
                fractionDigits={0}
              />
            </Box>
            <Box>
              <FieldLabel>Revenue share %</FieldLabel>
              <PercentInput
                size="sm"
                value={value.revenueSharePct}
                onChange={(revenueSharePct) => update({ revenueSharePct })}
                fractionDigits={0}
              />
            </Box>
            <Box>
              <FieldLabel hint="After an anchor miss, this share applies until the next anchor. If that next anchor is met, regular take resumes. Under MoM, this share applies only while the step stays under MoM.">
                Acceleration
              </FieldLabel>
              <PercentInput
                size="sm"
                value={value.accelerationPct}
                onChange={(accelerationPct) => update({ accelerationPct })}
                fractionDigits={0}
              />
            </Box>
          </SimpleGrid>
          <Text fontSize="sm" mt={3} className="accent-text">
            Normal take {value.fundingPct}% × {value.revenueSharePct}% = {collectionTake.toFixed(1)}%
            of cohort revenue.
          </Text>
        </Box>
      </Box>

      <Box className="card">
        <Flex className="section-header" align="center" gap={3}>
          <span className="step-num">4</span>
          <Text fontWeight="600">Deal</Text>
        </Flex>
        <Box p={5}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Box>
              <FieldLabel hint="Last payment is the residual that brings the balance to this monthly XIRR.">
                Target monthly XIRR %
              </FieldLabel>
              <PercentInput
                size="sm"
                value={value.monthlyXirrPct}
                onChange={(monthlyXirrPct) => update({ monthlyXirrPct })}
                fractionDigits={2}
              />
            </Box>
            <Box>
              <FieldLabel>UA amount</FieldLabel>
              <Input
                size="sm"
                value={fmtIntegerInput(value.uaAmount)}
                onChange={(event) =>
                  update({ uaAmount: Math.max(1, parseIntegerInput(event.target.value)) })
                }
              />
            </Box>
            <Box>
              <FieldLabel>Commitment</FieldLabel>
              <Input
                size="sm"
                value={fmtIntegerInput(value.commitmentAmount)}
                onChange={(event) =>
                  update({
                    commitmentAmount: Math.max(1, parseIntegerInput(event.target.value)),
                  })
                }
              />
            </Box>
            <Box>
              <FieldLabel hint="How many cohorts Twelve will fund. The ROAS vector runs until that cohort’s UA plus target XIRR is repaid, not this many months.">
                Months we fund
              </FieldLabel>
              <Input
                type="number"
                size="sm"
                min={1}
                step={1}
                value={value.fundingMonths}
                onChange={(event) =>
                  update({
                    fundingMonths: Math.max(1, Math.round(Number(event.target.value) || 1)),
                  })
                }
              />
            </Box>
          </SimpleGrid>
        </Box>
      </Box>
    </Box>
  );
}

FprInputs.propTypes = {
  value: PropTypes.shape({
    anchors: PropTypes.arrayOf(
      PropTypes.shape({
        month: PropTypes.number.isRequired,
        roasPct: PropTypes.number.isRequired,
      })
    ).isRequired,
    momStep: PropTypes.number.isRequired,
    momMode: PropTypes.string.isRequired,
    capPct: PropTypes.number.isRequired,
    borderlinePct: PropTypes.number.isRequired,
    fundingPct: PropTypes.number.isRequired,
    revenueSharePct: PropTypes.number.isRequired,
    monthlyXirrPct: PropTypes.number.isRequired,
    uaAmount: PropTypes.number.isRequired,
    commitmentAmount: PropTypes.number.isRequired,
    fundingMonths: PropTypes.number.isRequired,
    accelerationPct: PropTypes.number.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default FprInputs;
